import clsx from 'clsx';
import { format } from 'date-fns';
import { BanknotesIcon } from '@heroicons/react/24/outline';
import { formatDateSafe } from '@/utils/dateUtils';
import {
    CLIENT_CONFIRMED_BADGE,
    CLIENT_NOT_CONFIRMED_BADGE,
    getModalityLabel,
    getRequestStatusMetadata,
} from '../../vacations.definitions';
import { formatApprovalDeadline, resolveApprovalDeadline } from '../../utils/vacationApprovalDeadline';
import { ApprovalCardActions } from './components/ApprovalCardActions';
import { ApprovalCardHeader } from './components/ApprovalCardHeader';
import { ApprovalConflictNotice } from './components/ApprovalConflictNotice';
import { ApprovalSegmentList } from './components/ApprovalSegmentList';
import { CARD_LABELS, getDeadlineTone } from './vacationApprovalCard.definitions';
import type { VacationApprovalCardProps } from './vacationApprovalCard.types';

export function VacationApprovalCard({
    item,
    onDecide,
    isActive = false,
    className,
    typeLabel,
}: VacationApprovalCardProps) {
    const { request } = item;
    const status = getRequestStatusMetadata(request);
    const approvalDeadlineStr = formatApprovalDeadline(request.segments);
    const deadlineDate = resolveApprovalDeadline(request.segments);
    const deadline = getDeadlineTone(deadlineDate ? format(deadlineDate, 'yyyy-MM-dd') : undefined);
    const hasClient = request.clientApproval.status !== 'None';

    return (
        <article
            className={clsx(
                'flex h-full flex-col gap-4 rounded-2xl border bg-white p-5 shadow-sm transition-shadow',
                isActive
                    ? 'border-primary-300 ring-1 ring-primary-200'
                    : 'border-primary-100 hover:shadow-md',
                className
            )}
            aria-label={`Solicitação de férias de ${request.collaboratorName}`}
        >
            <ApprovalCardHeader
                name={request.collaboratorName}
                subtitle={
                    [item.collaboratorRole, item.collaboratorSquad].filter(Boolean).join(' · ') ||
                    request.collaboratorEmail
                }
                collaboratorId={request.collaboratorId}
            />

            <div className="flex flex-wrap items-center gap-2">
                {typeLabel && (
                    <span className="inline-flex w-fit items-center rounded-full bg-primary-50 px-2.5 py-1 text-[11px] font-semibold text-primary-700 font-montserrat">
                        {typeLabel}
                    </span>
                )}
                <span
                    className={clsx(
                        'inline-flex w-fit items-center rounded-full px-2.5 py-1 text-[11px] font-semibold font-montserrat',
                        status.className
                    )}
                >
                    {status.label}
                </span>
                {hasClient && (
                    <span
                        className={clsx(
                            'inline-flex w-fit items-center rounded-full px-2.5 py-1 text-[11px] font-semibold font-montserrat',
                            request.clientApproval.status === 'Approved'
                                ? 'bg-emerald-100 text-emerald-800'
                                : 'bg-amber-100 text-amber-800'
                        )}
                    >
                        {request.clientApproval.status === 'Approved'
                            ? CLIENT_CONFIRMED_BADGE
                            : CLIENT_NOT_CONFIRMED_BADGE}
                    </span>
                )}
            </div>

            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                <p className="text-sm font-semibold text-raro-gray-900 font-montserrat">
                    {getModalityLabel(request.modality)}
                </p>
                <p className="text-xs text-raro-gray-500 font-montserrat">
                    {request.totalDays} dias de gozo
                </p>
            </div>

            <ApprovalSegmentList
                segments={request.segments}
                dpSegmentDecisions={request.dpSegmentDecisions}
                clientSegmentDecisions={request.clientSegmentDecisions}
                requestStatus={request.status}
            />

            {request.sellRequest.requested && (
                <div className="flex items-center gap-2 rounded-xl border border-primary-200 bg-primary-50 px-3 py-2">
                    <BanknotesIcon
                        className="h-4 w-4 shrink-0 text-primary-600"
                        aria-hidden="true"
                    />
                    <p className="text-xs text-primary-800 font-montserrat">
                        {CARD_LABELS.sellRequestNotice(request.sellRequest.days, request.totalDays)}
                    </p>
                </div>
            )}

            <ApprovalConflictNotice conflicts={request.conflictsSnapshot} segments={request.segments} />

            <dl className="grid grid-cols-2 gap-3 border-t border-primary-100 pt-3">
                <div>
                    <dt className="text-[11px] uppercase tracking-wide text-raro-gray-400 font-montserrat">
                        {CARD_LABELS.requestedAt}
                    </dt>
                    <dd className="text-xs text-raro-gray-700 font-montserrat">
                        {formatDateSafe(request.requestedAt?.toDate?.())}
                    </dd>
                </div>
                <div>
                    <dt className="text-[11px] uppercase tracking-wide text-raro-gray-400 font-montserrat">
                        {CARD_LABELS.deadline}
                    </dt>
                    <dd className={clsx('text-xs font-montserrat', deadline.className)}>
                        {approvalDeadlineStr ?? '—'}
                        {deadline.hint ? ` · ${deadline.hint}` : ''}
                    </dd>
                </div>
            </dl>

            <ApprovalCardActions onDecide={(kind) => onDecide(item, kind)} />
        </article>
    );
}

export default VacationApprovalCard;
